import $ from 'jquery';
import WOW from 'wow.js';
import Vue from 'vue';
import 'lazysizes';
import 'lazysizes/plugins/rias/ls.rias';
import 'lazysizes/plugins/object-fit/ls.object-fit';
import 'lazysizes/plugins/blur-up/ls.blur-up';
import feather from 'feather-icons';

import ThemeBase from './ThemeBase'
import Section from './Section';
import Popup from './global/Popup';
import Newsletter from './global/Newsletter';
import CartDrawer from './global/CartDrawer';
import Drawer from './global/Drawer';

import LoginPage from './pages/LoginPage';

import TextAdverts from './sections/TextAdverts';
import Header from './sections/Header';
import Footer from './sections/Footer';
import Slideshow from './sections/Slideshow';
import Testimonial from './sections/Testimonial'
import Product from './sections/Product';
import FeaturedProduct from './sections/FeaturedProduct';
import ProductRecommendations from './sections/ProductRecommendations';
import ImageSection from './sections/Image';
import ImageWithTextOverlay from './sections/ImageWithTextOverlay';
import Collection from './sections/Collection';
import CollectionsList from './sections/CollectionsList';
import FeaturedCollection from './sections/FeaturedCollection';
import Cart from './sections/Cart';
import MapSection from './sections/MapSection';

import Dropdown from './components/Dropdown.vue';
import Video from './components/Video.vue'

/**
 * @typedef {Object} ShopifyEventDetail
 * @property {string} sectionId
 * @property {string} [blockId]
 * @property {boolean} [load]
 */

/**
 * @typedef {CustomEvent<ShopifyEventDetail>} ShopifyEvent
 */

 class Theme extends ThemeBase {
    RESIZE_DEBOUNCE_INTERVAL = 400;

    SECTIONS = {
        'header': Header,
        'footer': Footer,
        'collection--featured-collection': FeaturedCollection,
        'image--image': ImageSection,
        'image--slideshow': Slideshow,
        'image--image-with-text-overlay': ImageWithTextOverlay,
        'product--featured-product': FeaturedProduct,
        'store-information--map': MapSection,
        'template--collection': Collection,
        'template--collections-list': CollectionsList,
        'template--product': Product,
        'template--product--recommandations': ProductRecommendations,
        'template--cart': Cart,
        'text--testimonials': Testimonial,
        'text--text-adverts': TextAdverts,
        'video--video': Section,
        'video--video-hero': Section,
    }

    VUE_COMPONENTS = {
        'wetheme-dropdown': Dropdown,
        'wetheme-video': Video,
    }

    constructor() {
        super();

        this.name = 'Envy';

        this.$ = $;

        /* Section by it's unique ID
         * @type {Map<string, Section>}
         */
        this.sectionById = new Map();

        /** Array of sections by given section type
         * @type {Map<string, Section[]>}
         */
        this.sectionsByType = new Map();

        /** Array of registed components
         * @type {Array<import('./Component').default>}
         */
        this.registeredComponents = [];

        /** Array of initialized vue components
         * @type {Array<Object>}
         */
        this.vueComponents = [];

        /** @type {Array<{ resolve: (YoutubeApi) => void, reject: (string) => void }>} */
        this.youtubeLoadedCallbacks = [];
        /** @type {Array<{ resolve: (VimeoApi) => void, reject: (string) => void }>} */
        this.vimeoLoadedCallbacks = [];
        /** @type {Array<{ resolve: (MapsApi) => void, reject: (string) => void }>} */
        this.mapsLoadedCallbacks = [];

        this.resizeDebounceTimer = null;

        document.addEventListener('shopify:section:load', this.onSectionLoad);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.addEventListener('shopify:section:reorder', this.onSectionReorder);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener("DOMContentLoaded", this.onLoadAll);
        window.addEventListener('resize', this.onResize);
    }

    /** This will be execute when whole DOM is loaded */
    load() {
        this.loadGlobal();

        this.breakpoint = this.getBreakpoint();

        this.wow = new WOW().init();

        this.loadDrawers();

        this.popup = null;
        const popupEl = document.querySelector('#popup');
        if (popupEl && window.location.pathname !== '/challenge') {
            // Don't show popup when we're filling CAPTCHA for contact form
            this.popup = new Popup(this, popupEl);
        }

        this.loadIcons();

        // Newsletter form could be on contact-us page, so outside of sections
        this.newsletter = new Newsletter(this);

        // Login page doesn't have sections, load it here
        if (document.body.classList.contains('template-customers-login')) {
            this.login = new LoginPage(this);
        }

        // This is for all video embeds, even outside sections
        this.makeVideoEmbedsResponsive();

        this.gridSwatches();

    }

    loadDrawers = () => {
        if (!document.body.classList.contains('password-page')) {
            this.drawer = new Drawer(this);
            this.cartDrawer = this.drawer.slideouts.right ? new CartDrawer(this, this.drawer.slideouts.right.menu) : null;
        }
    }

    loadGlobal = () => {
        const globalEl = document.querySelector('#wetheme-global');
        if (!globalEl) {
            console.warn('Script #wetheme-global with global definitions not found');
            return;
        }

        let global;
        try {
            global = JSON.parse(globalEl.textContent);
        } catch (e) {
            console.warn('Invalid syntax in #wetheme-global', e);
            return;

        }
        this.translations = global.translations;
        this.moneyFormat = global.moneyFormat;
    }

    /**
     * Load vue components in `element` tree
     *
     * @param {HTMLElement} element
     */
    loadVueComponents = (element) => {
        Object.entries(this.VUE_COMPONENTS).forEach(
            ([tagName, VueComponent]) => {
                this.loadVueComponent(element, tagName, VueComponent);
            },
        );
    }

    /**
     * Load all instances of one vue component
     *
     * @param {HTMLElement} element Element which children should be search for vue components
     * @param {string} tagName
     * @param {*} Component
     */
    loadVueComponent = (element, tagName, Component) => {
        Array.prototype.forEach.call(
            element.querySelectorAll(tagName),
            (el) => {
                const attr = {
                    theme: this,
                };
                if (el.hasAttributes()) {
                    var attrs = el.attributes;
                    for (let i = attrs.length - 1; i >= 0; i--) {
                        const { name, value } = attrs[i];
                        if (value === 'true') {
                            attr[name] = true;
                        } else if (value === 'false') {
                            attr[name] = false;
                        } else {
                            attr[name] = value;
                        }
                    }
                }
                const c = new Vue({
                    el: el,
                    render: (h) => {
                        return h(Component, { attrs: attr });
                    },
                });
                this.vueComponents.push(c);
            }
        );
    }

    /**
     * Load section
     *
     * @param {HTMLElement} element Element to attach the section to
     * @param {typeof Section} SectionClass Section class to load
     * @param {boolean} pageLoad True on initial page load, False when the section reloads
     */
    loadSection = (element, SectionClass, pageLoad) => {
        const c = new SectionClass(this, element, pageLoad);
        this.sectionById.set(element.dataset.wethemeSectionId, c);
        const type = element.dataset.wethemeSectionType;
        let sections = this.sectionsByType.get(type);
        if (!sections) {
            sections = [];
            this.sectionsByType.set(type, sections);
        }
        sections.push(c);
        this.loadVueComponents(element)
    }

    /**
     * Load all sections that has given type
     *
     * @param {string} type Type of section to laod
     * @param {typeof Section} SectionClass Section class to load
     * @param {boolean} pageLoad True on initial page load, False when the section reloads
     */
    loadSectionsByType = (type, SectionClass, pageLoad) => {
        const selector = `[data-wetheme-section-type="${type}"]`;
        Array.prototype.forEach.call(
            document.querySelectorAll(selector),
            (element) => {
                try {
                    this.loadSection(element, SectionClass, pageLoad);
                } catch (e) {
                    console.error(`Unable to load section ${type} on element`, element, e);
                }
            },
        );
    }

    /**
     * Load all the sections
     */
    onLoadAll = () => {
        this.load();
        Object.entries(this.SECTIONS).forEach(
            ([ type, SectionClass ]) => {
                this.loadSectionsByType(type, SectionClass, true);
            },
        );
    }

    /**
     * This is called when section loads in Theme Editor, it'll initialize section by the sectionId param
     *
     * @param {ShopifyEvent} event
     */
    onSectionLoad = (event) => {
        const { sectionId } = event.detail;
        const selector = `[data-wetheme-section-id="${sectionId}"]`;
        const element = /** @type HTMLElement */ (document.querySelector(selector));
        if (!element) {
            console.warn(`Element with ${selector} not found, section won't be loaded`);
            return;
        }
        const type = element.dataset.wethemeSectionType;
        const SectionClass = this.SECTIONS[type];
        if (!SectionClass) {
            console.warn(`Cannot find class with type "${type}"`);
            return;
        }
        this.loadSection(element, SectionClass, false);
    }

    /**
     * This is called when section unloads in Theme Editor, it'll destroy a section by the sectionId param
     *
     * @param {ShopifyEvent} event
     */
    onSectionUnload = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, nothing will be unloaded`);
            return;
        }
        section.destroy({ element });
    }

    /**
     * This is called when section is selected (opened) by user in Theme Editor
     *
     * @param {ShopifyEvent} event
     */
    onSectionSelect = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId, load } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, nothing will be selected`);
            return;
        }
        section.onSectionSelect({ element, sectionId, load });
    }

    /**
     * This is called when section is deselected (closed) by user in Theme Editor
     *
     * @param {ShopifyEvent} event
     */
    onSectionDeselect = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, nothing will be deselected`);
            return;
        }
        section.onSectionDeselect({ element, sectionId });
    }

    /**
     * This is called when section is reorderd by user in Theme Editor
     *
     * @param {ShopifyEvent} event
     */
    onSectionReorder = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, nothing will be done on reorder`);
            return;
        }
        section.onSectionReorder({ element, sectionId });
    }

    /**
     * This is called when block is selected (opened) by user in Theme Editor
     *
     * @param {ShopifyEvent} event
     */
    onBlockSelect = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId, blockId, load } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, block select will do nothing`);
            return;
        }
        section.onBlockSelect({ element, sectionId, blockId, load });
    }

    /**
     * This is called when block is deselected (closed) by user in Theme Editor
     *
     * @param {CustomEvent} event
     */
    onBlockDeselect = (event) => {
        const element = /** @type HTMLElement */ (event.target);
        const { sectionId, blockId } = event.detail;
        const section = this.sectionById.get(sectionId);
        if (!section) {
            console.warn(`No section with id ${sectionId}, block deselect will do nothing`);
            return;
        }
        section.onBlockDeselect({ element, sectionId, blockId });
    }

    /**
     * This is called immediatelly when window is resized
     *
     * @param {Event} event
     */
    onResize = (event) => {
        const width = this.getWindowWidth();
        const oldWidth = this.previousWidth;

        // Trigger non-debounced event on all components
        this.registeredComponents.forEach(
            (c) => c.onWindowResizeRaw({ width })
        )

        // Trigger debounced event on all sections
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = setTimeout(() => {
            this.resizeDebounceTimer = null;

            const breakpoint = this.getBreakpoint(width);
            this.registeredComponents.forEach(
                (c) => {
                    c.onWindowResize({ width, oldWidth, breakpoint });
                    if (breakpoint !== this.breakpoint) {
                        // Breakpoint has changed, trigger the event
                        c.onWindowResizeBreakpoint({ breakpoint, oldBreakpoint: this.breakpoint, width });
                    }
                }
            );
            this.previousWidth = width;
            this.breakpoint = breakpoint;
        }, this.RESIZE_DEBOUNCE_INTERVAL);
    }

    loadIcons = (element = null) => {
        const parent = element || document;
        // If we use wrong icon, whole feather will crash and no further icons will be loaded
        // this will check if icons are vaild and will remove the feather if not
        var icons = Object.keys(feather.icons);
        Array.prototype.forEach.call(
            parent.querySelectorAll('[data-feather]'),
            function (el) {
                var icon = el.dataset.feather;
                if (icons.indexOf(icon) < 0) {
                    console.warn('No such icon "' + icon + '" in feather, element', el);
                    el.dataset.featherInvalid = icon;
                    delete el.dataset.feather;
                }
            }
        );
        try {
            feather.replace();
        } catch (e) {
            console.error('Unable to load icons:', e);
        }
    }

    loadYoutubeApi = () => {
        return new Promise((resolve, reject) => {
            if (window.YT) {
                // We already have youtube API loaded, we're done
                resolve(window.YT);
            } else if (!document.querySelector('#youtube_api')) {
                // Resolve once youtube is loaded
                this.youtubeLoadedCallbacks.push({ resolve, reject });

                // Youtube API is not yet loaded, create script that loads it
                this.createScript("youtube_api", "https://www.youtube.com/player_api", true);
            } else {
                // Script for loading youtube API is there, but youtube is not yet loaded
                this.youtubeLoadedCallbacks.push({ resolve, reject });
            }
        });
    }

    onYoutubeLoaded = () => {
        this.youtubeLoadedCallbacks.forEach(({ resolve }) => {
            resolve(window.YT);
        });
        this.youtubeLoadedCallbacks.splice(0);
    }

    loadVimeoApi = () => {
        return new Promise((resolve, reject) => {
            if (window.Vimeo) {
                // Already loaded
                resolve(window.Vimeo);
            } else if (!document.querySelector('#vimeo_api')) {
                // Vimeo API is not yet loaded, create script that loads it
                const script = this.createScript("vimeo_api", "https://player.vimeo.com/api/player.js");
                script.onload = () => {
                    resolve(window.Vimeo);
                    this.onVimeoLoaded();
                };
            } else {
                this.vimeoLoadedCallbacks.push({ resolve, reject });
            }
        });
    }

    onVimeoLoaded = () => {
        this.vimeoLoadedCallbacks.forEach(({ resolve }) => {
            resolve(window.Vimeo);
        });
        this.vimeoLoadedCallbacks.splice(0);
    }

    loadGoogleMapApi = (apiKey) => {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps && window.google.maps.Geocoder && window.google.maps.Map) {
                resolve(window.google.maps);
            } else if (!document.querySelector('#google_maps_api')) {
                const loadMaps = () => {
                    // Google Maps API is not yet loaded, create script that loads it
                    const url = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=onGoogleMapsAPIReady`;
                    this.createScript('google_maps_api', url, true);
                };

                // Resolve once Maps API is loaded
                this.mapsLoadedCallbacks.push({ resolve, reject });

                // Google Maps API collides with Shopify payments loading Google Pay,
                // we need to wait for payments to load before loading Maps API
                // TODO: remove once Shopify fixes their payments code
                let attemptsRemaining = 6;
                const checkForPayments = () => {
                    // Wait for checkout buttons to be loaded
                    const checkoutWrapper = document.querySelector('[data-shopify="dynamic-checkout-cart"]');
                    if (attemptsRemaining > 0 && checkoutWrapper && checkoutWrapper.children.length === 0) {
                        attemptsRemaining -= 1;
                        setTimeout(checkForPayments, 500);
                    } else {
                        loadMaps();
                    }
                }
                checkForPayments();

            } else {
                // Script for loading maps API is there, but it is not yet loaded
                this.mapsLoadedCallbacks.push({ resolve, reject });
            }
        });
    }

    onGoogleMapsLoaded = () => {
        this.mapsLoadedCallbacks.forEach(({ resolve }) => {
            resolve(window.google.maps);
        });
        this.mapsLoadedCallbacks.splice(0);
    }

    onGoogleAuthFailure = (reason) => {
        this.mapsLoadedCallbacks.forEach(({ reject }) => {
            reject(reason);
        });
        this.mapsLoadedCallbacks.splice(0);
    }

    updateCartDrawer = (cart) => {
        if (!this.cartDrawer) {
            return;
        }
        this.cartDrawer.updateCartDrawer(cart);
    }

    createScript = (id, src, async = false) => {
        const tag = document.createElement('script');
        tag.id = id;
        tag.src = src;
        if (async) {
            tag.defer = true;
            tag.async = true;
        }
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        return tag;
    }

    registerComponent = (component) => {
        this.registeredComponents.push(component);
    }

    makeVideoEmbedsResponsive = () => {
        $('.template-page iframe, .template-blog iframe, .template-article iframe').each(function(index, iframe){
            var source = $(iframe).attr("src");

            if (source.indexOf("youtube.com") >= 0 || source.indexOf("vimeo.com") >= 0 || source.indexOf("dailymotion.com") >= 0){
                //create container
                var embedContainer = document.createElement('div');
                $(embedContainer).addClass('embed-container');

                //clone iframe
                var iframeClone = $(iframe).clone();

                //put clone in container
                $(embedContainer).append(iframeClone);

                //embed clone with responsive container
                $(iframe).replaceWith(embedContainer);
            }
        });
    }

    gridSwatches = () => {
        $('.indiv-product__swatches').find('input').on('change', function () {
            var updateImage = $(this).attr('data-href');
            var targetImage = $(this).closest('.indiv-product').find('.grid__image img');

            targetImage.attr({ src: updateImage });
            targetImage.attr({ srcset: updateImage });
            targetImage.data({ lowsrc: updateImage });
            targetImage.data({ srcset: updateImage });

        });
    }
}

// Make the theme object instance globally available
window.wetheme = new Theme();

window.onYouTubePlayerAPIReady = () => {
    window.wetheme.onYoutubeLoaded();
};

window.onGoogleMapsAPIReady = () => {
    window.wetheme.onGoogleMapsLoaded();
}

window.gm_authFailure = function () {
    window.wetheme.onGoogleAuthFailure('Google Maps authentication error, API key might be invalid');
}
